import { useTranslation } from 'react-i18next';
import { GridAlignment, GridColDef } from '@mui/x-data-grid';
import VisibilityIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import { useState, ChangeEvent, useEffect, useContext } from 'react';
import { DELAY_BEFORE_SEARCH, MAX_PAGE_LIMIT } from '../../../constants/numbers';
import BaranDataGrid from '../../common/BaranDataGrid';
import { filterList } from '../../../utils/array';
import { useNavigate } from 'react-router-dom';
import { ITicket } from '../../../interfaces/support';
import IResponse, { IPagination } from '../../../interfaces/response';
import axios from 'axios';
import { useTicketStatusColors } from '../../../hooks/useTicketStatusColors';
import { useDebounce } from 'use-debounce';
import BasicModal from '../../common/BasicModal.tsx';
import { Button, Menu, MenuItem, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { exportToExcel } from '../../../utils/file.ts';
import { ITicketChat } from '../../../interfaces/chat.ts';
import { UserContext } from '../../../contexts/UserContext.tsx';

export default function TicketsListAdmin({ adminId }: { adminId?: string }) {
	const [pageSize, setPageSize] = useState(MAX_PAGE_LIMIT);
	const [page, setPage] = useState(1);
	const [searchTerm, setSearchTerm] = useState('');
	const [fetchedData, setfetchedData] = useState<ITicket[]>([]);
	const [pagination, setPagination] = useState<IPagination | null>();
	const [deleteTicket, setDeleteTicket] = useState<ITicket>();
	const [loading, setLoading] = useState(false);
	const [search] = useDebounce(searchTerm, DELAY_BEFORE_SEARCH);
	const [deleteLoading, setDeleteLoading] = useState(false);
	const [refresh, setRefresh] = useState(1);
	const [moreMenu, setMoreMenu] = useState<null | HTMLElement>(null);
	const [ticketId, setTicketIId] = useState<null | number>(null);
	const [audience, setAudience] = useState<ITicketChat>();
	const open = Boolean(moreMenu);
	const { user } = useContext(UserContext);

	const handleClick = (id: number) => (event: React.MouseEvent<HTMLButtonElement>) => {
		setMoreMenu(event.currentTarget);
		setTicketIId(id);
	};
	const handleClose = () => {
		setMoreMenu(null);
	};

	const headerClassNames = 'text-xs bg-background-paper';

	const { t } = useTranslation();
	const navigate = useNavigate();

	const handleViewTicket = (id: number) => () => {
		navigate(`/admin/support/view/${id}`);
	};
	const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	const createNewTicket = () => {
		navigate(`/support/new-ticket`);
	};
	const handleCloseDelete = (ticket: ITicket) => () => {
		setDeleteTicket(ticket);
	};
	const handleCloseDeleteDialog = () => {
		setDeleteTicket(undefined);
	};
	const handleExportExcel = () => {
		exportToExcel({ data: filteredRows, fileName: 'payment_list', t });
	};
	const closeTicket = () => {
		console.log(audience?.id);
		axios({
			url: user?.is_admin ? `/admin/tickets/close/${ticketId}` : `/tickets/close/${ticketId}`,
			method: 'POST',
		}).then(r => {
			if (r?.data?.success) {
				setAudience(r.data.data);
				setRefresh(prev => prev + 1);
			}
		});
	};

	const getServer = () => {
		setLoading(true);
		axios<IResponse<ITicket[]>>({
			url: '/admin/tickets',
			params: {
				page: page,
				limit: pageSize,
				search: searchTerm,
				user_id: adminId,
			},
		})
			.then(res => {
				if (res.data.success) {
					setPagination(res.data.pagination);
					setfetchedData(res.data.data.map(ticket => ({ ...ticket, ticket_id: ticket.id })));
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	useEffect(() => {
		getServer();
	}, [page, pageSize, refresh]);

	useEffect(() => {
		if (page === 1) {
			getServer();
		} else {
			setPage(1);
		}
	}, [search]);
	const handleDeleteTicket = () => {
		setDeleteLoading(true);
		axios<IResponse<any>>({
			url: `admin/tickets/${deleteTicket?.id}`,
			method: 'DELETE',
		})
			.then(res => {
				if (res.data.success) {
					setDeleteTicket(undefined);
					setRefresh(prev => prev + 1);
				}
			})
			.finally(() => {
				setDeleteLoading(false);
			});
	};

	const properties: (keyof ITicket)[] = ['title'];

	const filteredRows = filterList(fetchedData, properties, searchTerm);

	const columns: GridColDef<ITicket>[] = [
		{
			field: 'company',
			headerName: t('company_name'),
			width: 200,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'title',
			headerName: t('ticket_title'),
			width: 200,
			flex: 1,
			headerAlign: 'left',
			align: 'left',
			headerClassName: headerClassNames,
		},
		{
			field: 'status',
			headerName: t('status'),
			width: 180,
			cellClassName: 'flex items-center',
			headerAlign: 'center',
			headerClassName: `${headerClassNames} ps-12`,
			align: 'center',
			renderCell: params => {
				const isPending = params.row.status === 'pending';
				const label = isPending ? t('awaiting_reply') : t(params.row.status);
				const bgColorClass = useTicketStatusColors(params.value);
				return <div className={`ms-10 flex h-[min-content] w-[max-content] items-center rounded px-3 py-0.5 text-xs font-medium ${bgColorClass}`}>{label}</div>;
			},
		},
		{
			field: 'created_at',
			headerName: t('date'),
			width: 150,
			headerAlign: 'center',
			align: 'center',
			headerClassName: headerClassNames,
			renderCell: params => {
				return new Date(params.value).toLocaleDateString('fa-IR');
			},
		},
		{
			field: 'action',
			headerName: t('action'),
			minWidth: 200,
			headerAlign: 'right' as GridAlignment,
			align: 'right' as GridAlignment,
			headerClassName: `${headerClassNames}  pe-10`,
			renderCell: params => {
				return (
					<>
						{params.row.status !== 'closed' && (
							<IconButton onClick={handleClick(params.row.id)}>
								<MoreVertIcon className="text-text-secondary" />
							</IconButton>
						)}
						<IconButton aria-label="eye" onClick={handleViewTicket(params.row.id)}>
							<VisibilityIcon className="text-text-secondary" />
						</IconButton>
						<IconButton onClick={handleCloseDelete(params.row)}>
							<DeleteOutlineIcon className="text-text-secondary" />
						</IconButton>
					</>
				);
			},
		},
	];

	return (
		<div className={'w-full'}>
			<BaranDataGrid<ITicket> loading={loading} pagination={pagination} rows={fetchedData} columns={columns} pageSize={pageSize} setPageSize={setPageSize} page={page} setPage={setPage} searchTerm={searchTerm} handleSearch={handleSearch} onClick={fetchedData.length ? handleExportExcel : undefined} onClick2={createNewTicket} actionButtonLable2={t('create_new_ticket')} actionButtonLable={t('export_excel')} searchableFields={properties} isCreateBtn2 />;
			<BasicModal
				open={!!deleteTicket}
				onClose={handleCloseDeleteDialog}
				title={t('delete_role')}
				titleClassName={'text-center grow '}
				subClassName={'flex flex-row-reverse'}
				className={'max-w-sm rounded-lg'}
				showBackIcon={false}
				action={
					<>
						<Button onClick={handleCloseDeleteDialog} color="inherit">
							{t('cancel')}
						</Button>
						<LoadingButton loading={deleteLoading} onClick={handleDeleteTicket} variant="outlined" color="error">
							{t('delete')}
						</LoadingButton>
					</>
				}
			>
				<Typography>{t('delete_news_description')}</Typography>
			</BasicModal>
			<Menu
				anchorEl={moreMenu}
				open={open}
				onClose={handleClose}
				MenuListProps={{
					'aria-labelledby': 'basic-button',
				}}
			>
				<MenuItem onClick={handleViewTicket(ticketId!)}>{t('answer')}</MenuItem>
				<MenuItem onClick={closeTicket}>{t('close-ticket')}</MenuItem>
			</Menu>
		</div>
	);
}
